import h from 'hyperscript';
import type { Delta } from 'quill/core';

import { ProgressSpinner } from '@disy/ui/progress-spinner';
import { vr } from '@disy/ui/separator';
import { getSetting } from '@disy/workbook.lib/settings';

import type { AttributeWithObjecttype } from 'ui/attribute-label/attribute-formatting-utils';
import { attributeObjecttypeLabel } from 'ui/attribute-label/attribute-formatting-utils';
import { createAttributeLabel } from 'ui/attribute-label/attribute-label';

import type { ExternalLayerDescriptions } from 'cadenza/api-client/workbook-layer/workbook-layer-capabilities-dto';
import { getLayerSetting } from 'cadenza/map/context/workbook/layer-context-util';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import { getStore } from 'cadenza/store/cadenza-store';
import { DescriptionEditing } from 'cadenza/ui/description-editing/description-editing';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { isReferenceLine } from 'cadenza/workbook/api/data-view-visualization-key';
import { getWorkbookLayerDescriptions } from 'cadenza/workbook/api/external-layers-api';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { dataTypeLabel } from 'cadenza/workbook/attribute-element/data-type-icon';
import { getAllAttributeIdsFromDataView } from 'cadenza/workbook/data-view/data-view-utils';
import {
  hasAtLeastOneUsedAttribute,
  renderDataViewSummary,
  renderLayerSummary,
  shouldShowAttributeSummary,
} from 'cadenza/workbook/map/layer-info/layer-info-utils';
import { isExternalLayer } from 'cadenza/workbook/map/workbook-layer-type-util';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import { isLayerVisible } from 'cadenza/workbook/state/workbook-map-layer.state';
import {
  getAttributeById,
  getDataViewById,
  getViewById,
  getWorkbookNameAndHash,
  isCombinationChart,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';

import './view-info.css';

const COMPONENT_NAME = 'd-view-info';

export class ViewInfo extends HTMLElement {
  readonly #viewId: ViewId;
  constructor(viewId: ViewId) {
    super();
    this.classList.add(COMPONENT_NAME, 'd-stack-v', 'space-2');
    this.#viewId = viewId;
  }

  async connectedCallback() {
    const view = getViewById(getStore().getState(), this.#viewId);
    this.append(await createViewDetails(view));
    if (shouldLoadExternalLayerDescriptions(this.#viewId)) {
      const progressSpinner = new ProgressSpinner({ vertical: true });
      this.append(progressSpinner);
      const descriptions = await getWorkbookLayerDescriptions(
        getWorkbookNameAndHash(getStore().getState()),
        this.#viewId,
      );
      progressSpinner.replaceWith(...renderSummary(view, descriptions));
    } else {
      this.append(...renderSummary(view));
    }
  }
}

function createAttributeDetail(attribute: WorkbookAttributeDto) {
  const attributeDetailContainer = h(
    `.${COMPONENT_NAME}--attribute.d-stack-v.space-1`,
    createAttributeLabel(attribute.type, attribute.printName),
  );
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    attributeDetailContainer.append(createAttributeDescription(attribute));
  }
  if (attribute.description) {
    attributeDetailContainer.append(
      h('d-truncated-p', {
        fullText: attribute.description,
      }),
    );
  }
  return attributeDetailContainer;
}

function crateAttributesList(dataView: DataViewDto) {
  return getAllAttributeIdsFromDataView(dataView)
    .map((attrId) => getAttributeById(getStore().getState(), attrId))
    .flatMap((attribute) => {
      return createAttributeDetail(attribute);
    });
}

export function shouldShowViewDescription(view: ViewDto) {
  return (
    getSetting(view, CommonViewSettingsKey.toPersistenceKey('DESCRIPTION_SHOW_DESCRIPTION')) &&
    getSetting(view, CommonViewSettingsKey.toPersistenceKey('DESCRIPTION'))
  );
}

async function createViewDetails(view: ViewDto) {
  const descriptionDetails: HTMLElement[] = [h('h2.bold', view.name)];
  const viewDescription = getSetting<Delta>(view, CommonViewSettingsKey.toPersistenceKey('DESCRIPTION'));
  if (shouldShowViewDescription(view) && viewDescription) {
    const dataViewId = view.dataViews.length === 1 ? view.dataViews[0] : undefined;
    const descriptionEditing = new DescriptionEditing({
      dataViewId,
      readOnly: true,
    });
    descriptionEditing.description = viewDescription;
    descriptionDetails.push(descriptionEditing);
  }
  return h('.view-details.d-stack-v.space-2', descriptionDetails);
}

function createAttributeDescription(attribute: WorkbookAttributeDto) {
  return h('.attribute-description.d-stack-h.space-2.text-muted', [
    h('span', AttributeCategory.i18n(attribute.category, { count: 1 })),
    vr(),
    h('.attribute-description--datatype', dataTypeLabel(attribute)),
    vr(),
    h('.attribute-description--objecttype-name', [attributeObjecttypeLabel(attribute as AttributeWithObjecttype)]),
  ]);
}

function renderSummary(view: ViewDto, externalDescriptions?: ExternalLayerDescriptions) {
  if (view.type === 'MAP') {
    return renderMapSummary(view, externalDescriptions).filter((element): element is HTMLElement => !!element);
  } else if (isCombinationChart(getStore().getState(), view.id)) {
    return view.dataViews
      .toReversed()
      .map((dataViewId) => getDataViewById(getStore().getState(), dataViewId))
      .map((dataView) => renderDataViewSummary(dataView.id))
      .filter((el) => el != null);
  } else {
    return [h('.d-stack-v.space-2', ...renderNotMapSummary(view))];
  }
}

function renderMapSummary(view: ViewDto, externalDescription?: ExternalLayerDescriptions) {
  return view.layerTree
    .filter((layer) => isLayerVisible(getStore().getState(), view.id, layer.id))
    .map((layerId) => renderLayerSummary(layerId.id, externalDescription));
}

function renderNotMapSummary(view: ViewDto) {
  const dataViews = view.dataViews
    .map((dataViewId) => getDataViewById(getStore().getState(), dataViewId))
    // reference line should not affect view info description
    .filter((dataView) => !isReferenceLine(dataView.visualizationKey));
  if (shouldShowAttributeSummary(view) && dataViews.some((dataView) => hasAtLeastOneUsedAttribute(dataView))) {
    return dataViews.flatMap((dataView) => crateAttributesList(dataView));
  }
  return [];
}

function shouldLoadExternalLayerDescriptions(viewId: ViewId) {
  const view = getViewById(getStore().getState(), viewId);
  if (view.type !== 'MAP') {
    return false;
  }
  const externalLayers = Object.values(view.layerDefinitions ?? []).filter((layer) => isExternalLayer(layer));
  if (!externalLayers.length) {
    return false;
  }
  return externalLayers.some(
    (layer) =>
      getLayerSetting<boolean>(layer, 'INFO_WINDOW_SHOW_DESCRIPTION') && !getLayerSetting(layer, 'DESCRIPTION'),
  );
}

customElements.define(COMPONENT_NAME, ViewInfo);
